



































































































import _ from 'lodash'
import Component from 'vue-class-component'
import Axios from 'axios'
import KeyValuePair from '@/modules/core/support/KeyValuePair'
import WidgetMixin from '@/modules/widget/mixins/WidgetMixin'
import {mixins} from 'vue-class-component/lib/util'

/**
 * Define la respuesta retornada por la API de OpenWeatherMap
 *
 * @see https://openweathermap.org/current
 */
interface WeatherState {
    /**
     * Según la documentación de la API es un parámetro interno
     */
    base: string

    /**
     * Coordenadas de la ciudad de la que se esta mostrado el tiempo
     * meteorológico
     */
    coord: {
        lon: number,
        lat: number,
    }

    /**
     * Información meteorológica de la ciudad
     */
    weather: Array<{
        id: number,
        main: string,
        description: string,
        icon: string,
    }>

    /**
     * Información principal, temperatura, humedad, presión atmosférica
     */
    main: {
        temp: number,
        pressure: number,
        humidity: number,
        temp_min: number,
        temp_max: number,
        sea_level: number,
        grnd_level: number,
    }

    /**
     * Información sobre el viento en la zona, velocidad y dirección
     */
    wind: {
        speed: number,
        deg: number,
    }

    /**
     * Información sobre la nubosidad de la zona
     */
    clouds: {
        all: number,
    },

    /**
     * Información sobre el volumen de lluvias de la zona
     */
    rain: {
        '1h': number,
        '3h': number,
    }

    snow: {
        '1h': number,
        '3h': number,
    }

    /**
     * Fecha en formato Unix del momento en el que se obtuvo y calculó la
     * información meteorológica
     */
    dt: number
}

/**
 * Widget para el dashboard que muestra el tiempo
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
@Component({
    filters: {
        fixed (value: any) {
            if (typeof value === 'string') {
                value = parseFloat(value)
            }

            return value.toFixed(1)
        },
    },
})
export default class WeatherWidget extends mixins(WidgetMixin) {
    /**
     * Cliente Http para realizar las peticiones a la API de OpenWeatherMap
     */
    private http = Axios.create({
        baseURL: '//api.openweathermap.org/data/2.5/',
        params: {
            appid: '3d58048a775c8c43fc0222fb706e1965',
            units: 'metric',
            lang: 'es',
        },
    })

    public get weather (): WeatherState | null {
        return this.widget.state.weather
    }

    public position: Position | null = null

    public settings = false
    public loadingPosition = false
    public loadingWeatherData = false

    /**
     * Obtiene las coordenadas actuales del usuario
     */
    public get coordinates (): {latitude: number, longitude: number} {
        return {
            latitude: this.get('coordinates.latitude', 0),
            longitude: this.get('coordinates.longitude', 0),
        }
    }

    public set locationMode (val: string) {
        this.set('locationMode', val)

        if (val === 'geolocation') {
            this.set('postalCode', '')
            this.loadGeolocation()
        }
    }

    /**
     * Obtiene del estado del widget el modo de geolocalización del usuario
     */
    public get locationMode () {
        return this.widget.state.locationMode || 'geolocation'
    }

    public locationModes = [
        { key: 'geolocation', text: 'Geolocalización'},
        { key: 'postalCode', text: 'Código Postal'},
    ]

    public postalCode = ''
    public cities = {}

    public iconMap: KeyValuePair = {
        '04d': 'wi-day-cloudy',
        '04n': 'wi-night-alt-cloudy',
        '02d': 'wi-cloud',
        '10d': 'wi-day-sleet',
        '01d': 'wi-day-sunny',
        '01n': 'wi-night-clear',
    }

    /**
     * Indica si el Widget se encuentra en un estado de error
     */
    public error: false | string = false

    /**
     * Muestra la temperatura principal, redondeada, sin decimales
     */
    public get roundedMainTemp () {
        return Math.round(this.weather!.main.temp)
    }

    public get icon () {
        if (!this.weather) {
            return ''
        }

        return this.iconMap[this.weather.weather[0].icon] || 'wi-na'
    }

    /**
     * Muestra la descripción del tiempo
     */
    public get weatherDescription () {
        if (!this.weather!.weather.length) {
            return 'No disponible'
        }

        const value = this.weather!.weather[0].description.toString()

        return value.charAt(0).toUpperCase() + value.slice(1)
    }

    public async saveSettings () {
        await this.syncState()
        this.settings = false
        await this.loadWeatherData()
    }

    /**
     * Carga la información meteorológica
     */
    public async loadWeatherData () {
        this.loadingWeatherData = true
        const params: KeyValuePair = {}

        switch (this.locationMode) {
            case 'geolocation':
                try {
                    this.position = await this.loadGeolocation()

                    params.lat = this.position.coords.latitude.toString()
                    params.lon = this.position.coords.longitude.toString()
                } catch (e) {
                    this.error = 'No se pudo obtener la ubicación'
                    this.loadingWeatherData = false

                    return
                }
                break
            case 'postalCode':
                const postalCode = this.widget.state.postalCode
                if (!postalCode) {
                    this.error = 'No se ha definido ningún código postal'

                    return
                }

                params.q = `${postalCode},es`
                break;
        }

        try {
            const response = await this.http.get('weather', { params })
            this.set('weather', response.data)
            this.syncState()
        } catch (e) {
            const reason = _.get(e, 'response.data.message', e.message)
            this.error = `Error al contactar con el servicio: ${reason}`
        }

        this.loadingWeatherData = false
    }

    /**
     * Carga la localización del usuario
     */
    public async loadGeolocation () {
        this.loadingPosition = true
        if (false === 'geolocation' in navigator) {
            throw new Error('El navegador no soporta la geolocalización')
        }

        return new Promise<Position>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
                async (p) => {
                    resolve(p)
                    this.set('coordinates', p.coords)
                    await this.syncState()
                    this.loadingPosition = false
                },
                (e) => {
                    reject(e)
                    this.loadingPosition = false
                },
            )
        })
    }

    /**
     * Al crear el componente solicita al servicio meteorológico la información sobre
     * el tiempo en la zona
     */
    public async created () {
        await this.loadWeatherData()
    }

    public get googleMapsSrc () {
        const { latitude, longitude } = this.coordinates

        return `https://maps.google.com/maps?hl=es;z=14&output=embed&q=${latitude},${longitude}`
    }
}
